import { selectTimeline, selectTripLists } from '../data/selectors';
import { getState } from '../data/store';

/**
 * Work the next screen would otherwise do on the tap, done while the app sits idle after
 * launch: timelines for the trips I'm likely to open, and the first use of each font weight
 * and width (creating a variable-font instance costs a few ms of layout the first time).
 */

const idle = (fn: (deadline?: IdleDeadline) => void) =>
  typeof requestIdleCallback === 'function'
    ? requestIdleCallback(fn, { timeout: 2000 })
    : setTimeout(() => fn(), 200);

const TYPE_CLASSES = [
  't-hero',
  't-display',
  't-title1',
  't-title2',
  't-title3',
  't-headline',
  't-callout',
  't-body',
  't-subhead',
  't-footnote',
  't-caption',
];
const WEIGHTS = [200, 400, 500, 600, 700];
const WIDTHS = ['100%', '112.5%', '125%'];

function fontSamples(): HTMLElement[] {
  const out: HTMLElement[] = [];
  const span = (className: string, style = '') => {
    const el = document.createElement('span');
    el.className = className;
    if (style) el.style.cssText = style;
    el.textContent = 'Fly 0123456789 Minneapolis';
    out.push(el);
  };
  for (const c of TYPE_CLASSES) {
    span(c);
    span(`${c} num`);
  }
  for (const w of WEIGHTS) for (const s of WIDTHS) span('', `font-weight:${w};font-stretch:${s}`);
  return out;
}

function warmFonts() {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    'position:fixed;left:0;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;contain:strict';
  document.body.append(host);
  const queue = fontSamples();
  const step = (deadline?: IdleDeadline) => {
    do {
      const el = queue.shift();
      if (!el) break;
      host.append(el);
      void el.offsetWidth; // Lay out this sample now, inside the idle slice.
    } while (deadline && deadline.timeRemaining() > 8);
    if (queue.length) idle(step);
    else host.remove();
  };
  idle(step);
}

function warmTimelines() {
  const s = getState();
  const { current, upcoming } = selectTripLists(s);
  for (const t of [...current, ...upcoming.slice(0, 3)]) selectTimeline(s, t.id);
}

export function warmUp() {
  idle(() => {
    warmTimelines();
    warmFonts();
  });
}
